"use client";

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { PercentageOfNewCustomersByZipCodeData } from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchStochasticDashboardData/types";
import { generateColors } from "@/modules/stochastic/features/Reports/SCDashboard/utils/chart";
import { ChartRangeSlider } from "@/modules/stochastic/features/Reports/SCDashboard/components/Chart/TotalHouseholdsByZipCodeChart/ChartRangeSlider";
import { ChartMetricCard } from "@/modules/stochastic/features/Reports/SCDashboard/components/ChartMetricCard/ChartMetricCard";
import ChartTooltipWithColoredYears from "@/modules/stochastic/features/Reports/SCDashboard/components/ChartTooltipWithColoredYears/ChartTooltipWithColoredYears";
import { ChartBarLegendWithColoredYear } from "@/modules/stochastic/features/Reports/SCDashboard/components/ChartBarLegendWithColoredYear/ChartBarLegendWithColoredYear";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

type PercentageOfNewCustomersByZipCodeChartProps = {
  chartId?: string;
  initialData: PercentageOfNewCustomersByZipCodeData;
};

export const PercentageOfNewCustomersByZipCodeChart = ({
  initialData,
}: PercentageOfNewCustomersByZipCodeChartProps) => {
  if (!initialData?.items || initialData.items.length === 0) return null;

  const { theme } = useTheme();
  const {
    years: allYears,
    totalCount,
    minAvgPercentage,
    maxAvgPercentage,
  } = initialData;
  const colors = generateColors(allYears, theme);

  const [yAxisRange, setYAxisRange] = useState<[number, number]>([
    minAvgPercentage,
    maxAvgPercentage,
  ]);
  const [barRange, setBarRange] = useState<[number, number]>([
    1,
    initialData.items.length,
  ]);

  const filteredByYAxis = initialData.items.filter(
    (item) =>
      item.avgPercentage >= yAxisRange[0] &&
      item.avgPercentage <= yAxisRange[1],
  );

  useEffect(() => {
    setBarRange([1, filteredByYAxis.length || 1]);
  }, [yAxisRange]);

  const handleYAxisChange = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      setYAxisRange(value);
    }
  };

  const handleBarRangeChange = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      setBarRange(value);
    }
  };

  const effectiveEnd = Math.min(barRange[1], filteredByYAxis.length);
  const displayData = filteredByYAxis.slice(barRange[0] - 1, effectiveEnd);

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Percentage of New Customers by ZIP Code
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Indicates where new market entry is occurring most
                  consistently.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          New customer volume by neighborhood
        </p>
      </CardHeader>

      <CardContent>
        <div className="flex">
          <div className="flex items-stretch justify-start mr-4">
            <ChartRangeSlider
              className="h-full"
              max={maxAvgPercentage}
              min={minAvgPercentage}
              onChange={handleYAxisChange}
              orientation="vertical"
              tooltipLabel="Avg Percentage (%)"
              value={yAxisRange}
            />
          </div>

          <div className="flex-1">
            <ResponsiveContainer height={500} width="100%">
              <BarChart
                data={displayData}
                margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
              >
                <CartesianGrid
                  stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
                  strokeDasharray="3 3"
                />
                <XAxis
                  angle={-90}
                  dataKey="postalCode"
                  fontSize={14}
                  height={100}
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  textAnchor="end"
                />
                <YAxis
                  label={{
                    value: "Percentage of New Customers (%)",
                    angle: -90,
                    position: "insideLeft",
                    offset: 0,
                    fill: theme === "dark" ? "#9CA3AF" : "#4B5563",
                    style: { textAnchor: "middle", fontSize: 18 },
                  }}
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  tickFormatter={(value) => `${value}%`}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipWithColoredYears
                      colors={colors}
                      labelKey="postalCode"
                      labelPrefix="ZIP:"
                      valueSuffix="%"
                    />
                  }
                />
                <Legend content={<ChartBarLegendWithColoredYear />} />
                <ReferenceLine stroke="#ccc" y={0} />
                {allYears.map((year) => (
                  <Bar
                    dataKey={year}
                    fill={colors[year]}
                    isAnimationActive={true}
                    key={year}
                    name={year}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-8 px-2">
              <ChartRangeSlider
                max={filteredByYAxis.length}
                min={1}
                onChange={handleBarRangeChange}
                orientation="horizontal"
                value={[
                  barRange[0],
                  Math.min(barRange[1], filteredByYAxis.length),
                ]}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-6 mt-6">
          <ChartMetricCard
            label="Bars Shown"
            value={`${barRange[0]} – ${effectiveEnd}`}
          />
          <ChartMetricCard label="Visible ZIPs" value={displayData.length} />
          <ChartMetricCard label="Total ZIPs" value={totalCount} />
          <ChartMetricCard
            label="Years Tracked"
            value={`${allYears[0]}–${allYears.at(-1)}`}
          />
        </div>
      </CardContent>
    </Card>
  );
};
